import { addProduct } from 'gtc-free-gift';


const CLASSES = {
    hidden: 'hidden',
};

class GTCFreeGiftProgress extends HTMLElement {

    constructor() {
        super();

        this.dom = {
            description: this.querySelector('[data-description]'),
            descriptionSuccess: this.querySelector('[data-description-success]'),
            remainingAmount: this.querySelector('[data-remaining-amount]'),
            addToCartButton: this.querySelector('[data-add-to-cart]'),
        }

        this.settings = {
            threshold: this.dataset.threshold,
            progress: this.dataset.progress,
            remainingAmount: 0,
            freeGiftItemManuallyRemoved: false,
            freeGiftItemId: this.dataset.variantId,
            handle: this.dataset.handle,
        }

        // Store bound function reference to prevent memory leaks
        this.getCustomCartData = this.getCustomCartData.bind(this);
        this.addToCart = this.addToCart.bind(this);

        // Add debouncing for cart changes
        this.debounceTimer = null;
        this.debounceDelay = 100; // 0.1 seconds debounce

        // Cache for cart data
        this.cartDataCache = null;
        this.cacheExpiry = 300; // 0.3 seconds cache
        this.lastCacheTime = 0;
    }

    connectedCallback() {
        this.addEventListeners();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    addEventListeners() {
        document.addEventListener("cart:change", this.getCustomCartData);
        document.addEventListener("free-gift:refresh", this.getCustomCartData);
        if (this.dom.addToCartButton) {
            this.dom.addToCartButton.addEventListener("click", this.addToCart);
        }
    }

    removeEventListeners() {
        document.removeEventListener("cart:change", this.getCustomCartData);
        document.removeEventListener("free-gift:refresh", this.getCustomCartData);
        if (this.dom.addToCartButton) {
            this.dom.addToCartButton.removeEventListener("click", this.addToCart);
        }
    }

    updateProgress() {
        this.style.setProperty("--progress", this.settings.progress);
    }

    updateRemainingAmount(remainingAmount) {
        const formattedAmount = this.formatMoney(remainingAmount, `{{ amount_no_decimals_with_comma_separator }} ${ window.themeVariables.settings.currencyCode }`);

        // Only update if the value has changed
        if (this.settings.remainingAmount !== formattedAmount) {
            this.settings.remainingAmount = formattedAmount;
            this.dom.remainingAmount.innerHTML = this.settings.remainingAmount;
        }
    }

    updateDescription() {
        const isComplete = this.settings.progress >= 100;

        // Only update if the completion state has changed
        if (isComplete) {
            this.dom.description.classList.add(CLASSES.hidden);
            this.dom.descriptionSuccess.classList.remove(CLASSES.hidden);
        } else {
            this.classList.remove(CLASSES.hidden);
            this.dom.description.classList.remove(CLASSES.hidden);
            this.dom.descriptionSuccess.classList.add(CLASSES.hidden);
        }
    }

    async addToCart(event) {
        await this.removeManualGiftItemAttribute();
        addProduct({ variantId: this.settings.freeGiftItemId, event: event, properties: { "_gift_item": this.settings.handle } });
    }

    async removeManualGiftItemAttribute() {
        const GIFT_REMOVAL_ATTRIBUTE = 'gift_manually_removed_' + this.settings.handle;
        const createRequestConfig = (body = null) => ({
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            ...(body && { body: JSON.stringify(body) })
        });
        const requestConfig = createRequestConfig({
            attributes: {
                [GIFT_REMOVAL_ATTRIBUTE]: null
            }
        });

        const response = await fetch(`${ window.Shopify.routes.root }cart/update.js`, requestConfig);

        if (!response.ok) {
            console.error('Failed to update cart with gift removal attribute');
        }
    }

    async getCustomCartData() {
        // Clear existing debounce timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // Debounce the API call
        this.debounceTimer = setTimeout(async () => {
            try {
                const data = await this.fetchCartData();
                if (data) {
                    this.updateFromData(data);
                }
            } catch (error) {
                console.error('Error in debounced cart data fetch:', error);
            }
        }, this.debounceDelay);
    }

    async fetchCartData() {
        try {
            // Check cache first
            const now = Date.now();
            if (this.cartDataCache && (now - this.lastCacheTime) < this.cacheExpiry) {
                // Find the matching item in the cached data
                const match = this.cartDataCache.find(data => data.handle === this.settings.handle);
                return match || null;
            }

            const response = await fetch('/cart?view=free-gift-data');
            if (!response.ok) throw new Error('Failed to fetch cart data');
            const data = await response.json();

            // Update cache
            this.cartDataCache = data;
            this.lastCacheTime = now;

            // Find the matching item in the data
            const match = data.find(data => data.handle === this.settings.handle);
            if (match == null) {
                return null;
            }

            return match;
        } catch (error) {
            console.error('Error fetching cart data:', error);
            return null;
        }
    }


    updateFromData(data) {
        const oldProgress = this.settings.progress;
        this.settings.progress = data.progress;
        this.settings.freeGiftItemManuallyRemoved = data.item_manually_removed;

        if (this.settings.freeGiftItemManuallyRemoved == true) {
            this.classList.remove(CLASSES.hidden);
        }

        // Only update if progress has changed
        if (oldProgress !== this.settings.progress) {
            this.updateProgress();
        }

        this.updateRemainingAmount(data.remaining_amount);
        this.updateDescription();

        this.updateAddToCartButton();
    }


    updateAddToCartButton() {
        this.dom.addToCartButton.classList.toggle(CLASSES.hidden, !this.settings.freeGiftItemManuallyRemoved);
    }


    formatMoney(cents, format = "") {
        if (typeof cents === "string") {
            cents = cents.replace(".", "");
        }

        const formatString = format || window.themeVariables.settings.moneyFormat;
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const match = formatString.match(placeholderRegex);

        if (!match) return formatString;

        const formatType = match[1];
        const value = this.formatWithDelimiters(cents, formatType);

        return formatString.replace(placeholderRegex, value);
    }

    formatWithDelimiters(cents, formatType) {
        if (isNaN(cents) || cents == null) return "0";

        const number = cents / 100;
        const config = this.getFormatConfig(formatType);

        const formatted = number.toFixed(config.precision);
        const parts = formatted.split(".");
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${ config.thousands }`);
        const centsAmount = parts[1] ? config.decimal + parts[1] : "";

        return dollarsAmount + centsAmount;
    }

    getFormatConfig(formatType) {
        const configs = {
            "amount": { precision: 2, thousands: ",", decimal: "." },
            "amount_no_decimals": { precision: 0, thousands: ",", decimal: "." },
            "amount_with_space_separator": { precision: 2, thousands: " ", decimal: "." },
            "amount_with_comma_separator": { precision: 2, thousands: ".", decimal: "," },
            "amount_with_apostrophe_separator": { precision: 2, thousands: "'", decimal: "." },
            "amount_no_decimals_with_comma_separator": { precision: 0, thousands: ".", decimal: "," },
            "amount_no_decimals_with_space_separator": { precision: 0, thousands: " ", decimal: "." },
            "amount_no_decimals_with_apostrophe_separator": { precision: 0, thousands: "'", decimal: "." }
        };

        return configs[formatType] || configs["amount"];
    }
}

// Register the custom element
customElements.define('gtc-free-gift-progress', GTCFreeGiftProgress);
